// Operator vocabulary for pedigree data-overlay criteria (TGCLOUD-1670).
//
// This list is EXACTLY the server executor's `SUPPORTED_OPS`
// (backend/util/crossEntityFilter.js:31-44). A parity test asserts it, because a
// second vocabulary is precisely how the Data Explorer's natural-language path
// broke: its plans emit eq / neq / before / after / between, `predicateToMongo`
// throws on them, and the failure surfaces as a success snackbar over a stale
// grid. Overlays speak the executor's language so a future server-side
// cross-family evaluator needs no translation layer.
//
// The client is deliberately NOT byte-identical to `predicateToMongo` in one
// respect: string equality here is case-insensitive and trimmed. Statuses are
// stored raw and verbatim (process-model.js:32-33), imports accept any string
// and an unvalidated PUT can write anything, so a row saying 'CLOSED' must match
// 'Closed'. A server-side evaluator would need a collation or an anchored regex
// to agree; that is recorded so nobody later assumes parity.

export const SUPPORTED_OPS = Object.freeze([
  'equals',
  'notEquals',
  'in',
  'notIn',
  'exists',
  'notExists',
  'contains',
  'startsWith',
  'greaterThan',
  'lessThan',
  'greaterThanOrEqual',
  'lessThanOrEqual'
]);

const OPS = new Set(SUPPORTED_OPS);

/** Operators that carry no `value`. */
export const VALUELESS_OPS = Object.freeze(['exists', 'notExists']);

/** Human labels, matching the Data Explorer's filter panel wording. */
export const OP_LABELS = Object.freeze({
  equals: 'is',
  notEquals: 'is not',
  in: 'is any of',
  notIn: 'is none of',
  exists: 'is present',
  notExists: 'is missing',
  contains: 'contains',
  startsWith: 'starts with',
  greaterThan: 'is after',
  lessThan: 'is before',
  greaterThanOrEqual: 'is on or after',
  lessThanOrEqual: 'is on or before'
});

export const isSupportedOp = (op) => OPS.has(op);

const norm = (v) => String(v).trim().toLowerCase();

/** Present means: not null, not undefined, not an empty or whitespace string, not an empty array. */
function isPresent(v) {
  if (v == null) return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'string') return v.trim() !== '';
  if (v instanceof Date) return !Number.isNaN(v.getTime());
  return true;
}

function looseEquals(actual, expected) {
  if (expected === null) return !isPresent(actual);
  if (typeof expected === 'boolean') return actual === expected;
  if (typeof expected === 'number') {
    const n = typeof actual === 'number' ? actual : Number(actual);
    return Number.isFinite(n) && n === expected;
  }
  if (!isPresent(actual)) return false;
  return norm(actual) === norm(expected);
}

function compare(actual, expected) {
  const an = typeof actual === 'number' ? actual : Number(actual);
  const en = typeof expected === 'number' ? expected : Number(expected);
  if (Number.isFinite(an) && Number.isFinite(en)) return an - en;
  return String(actual).localeCompare(String(expected));
}

/** notEquals over an array means NO element matches, so flip to the positive test. */
const invert = (op) => (op === 'notEquals' ? 'equals' : 'in');

const toList = (expected) => (Array.isArray(expected) ? expected : [expected]);

/**
 * Apply one operator to one already-resolved value.
 *
 * `actual` may be an array (a dotted path over a member array), in which case
 * positive operators match when ANY element matches and negative operators
 * match when NO element matches, mirroring Mongo.
 *
 * Missing-value semantics, which are the ones that bite:
 *   notEquals / notIn MATCH a missing value. A boolean with no schema default is
 *   `undefined`, never `false`, so `notEquals true` is the only correct way to
 *   ask "not flagged" (clinical-note-model.js:14-25 declare bare Booleans).
 */
export function applyOperator(op, actual, expected) {
  if (!OPS.has(op)) {
    throw new Error(`Unsupported data-overlay operator: ${op}`);
  }

  if (Array.isArray(actual) && op !== 'exists' && op !== 'notExists') {
    const negative = op === 'notEquals' || op === 'notIn';
    const hit = actual.some((el) =>
      applyOperator(negative ? invert(op) : op, el, expected)
    );
    return negative ? !hit : hit;
  }

  switch (op) {
    case 'exists':
      return isPresent(actual);
    case 'notExists':
      return !isPresent(actual);
    case 'equals':
      return looseEquals(actual, expected);
    case 'notEquals':
      return !looseEquals(actual, expected);
    case 'in':
      return toList(expected).some((e) => looseEquals(actual, e));
    case 'notIn':
      return !toList(expected).some((e) => looseEquals(actual, e));
    case 'contains':
      return isPresent(actual) && norm(actual).includes(norm(expected));
    case 'startsWith':
      return isPresent(actual) && norm(actual).startsWith(norm(expected));
    case 'greaterThan':
      return isPresent(actual) && compare(actual, expected) > 0;
    case 'lessThan':
      return isPresent(actual) && compare(actual, expected) < 0;
    case 'greaterThanOrEqual':
      return isPresent(actual) && compare(actual, expected) >= 0;
    case 'lessThanOrEqual':
      return isPresent(actual) && compare(actual, expected) <= 0;
    default:
      return false;
  }
}
